<template>
  <div class="companies-trust">
    <MoleculeRatingStars
      v-if="hasRatingSlot"
      :hover="hover"
    >
      <template #rating>
        <slot name="rating" />
      </template>
    </MoleculeRatingStars>
    <div
      class="companies"
      :class="{ personalized: forPersonalization }"
    >
      <div
        v-for="(company, index) in companies"
        :key="index"
        class="logo"
      >
        <UiComponentImage
          :alt="company.alt"
          contains
          :expected-size="48"
          :height="company.height"
          :extra-params="{ ...company.params }"
          :orientation="
            company.width > company.height ? 'portrait' : 'landscape'
          "
          :source="company.url"
          use-natural-width
          :width="company.width"
        />
      </div>
      <div
        v-if="forPersonalization"
        class="logo customizable"
        @click="onClick"
      >
        <div class="placeholder">
          <UiComponentImage
            alt="plus"
            class="plus"
            :expected-size="24"
            :height="24"
            source="https://livestorm.imgix.net/1127/1656063210-plus.svg"
            :width="24"
          />
          <UiComponentHeading
            as="paragraph"
            :level="5"
          >
            <slot name="experience" />
          </UiComponentHeading>
          <img
            alt="Your company"
            class="your-company-logo"
            src="https://livestorm.imgix.net/1127/1632240333-miro-logo.png"
          >
        </div>
      </div>
    </div>
    <!-- <UiComponentParagraph class="headline"><slot name="heading" /></UiComponentParagraph> -->
    <renderNode />
    <div
      v-if="!!callToAction"
      class="action"
    >
      <AtomButton
        :action="callToAction.action"
        :label="callToAction.label"
        size="l"
        :to="callToAction.to"
      >
        {{ callToAction.slot }}
      </AtomButton>
    </div>
  </div>
</template>

<script setup>
// Props
const props = defineProps({
  callToAction: {
    default() {
      return undefined
    },
    required: false,
    type: Object,
  },
  companies: {
    required: true,
    type: Array,
  },
  forPersonalization: {
    default() {
      return false
    },
    required: false,
    type: Boolean,
  },
  hover: {
    default() {
      return undefined
    },
    required: false,
    type: String,
  },
  type: {
    default() {
      return 'modal-form'
    },
    required: false,
    type: String,
  },
})

// Composable
const { track } = useAnalyticsJS()
const slots = useSlots()

// States
const forms = useState('forms')

// Computed Properties
const hasRatingSlot = computed(() => {
  const nodes = slots && slots.rating && slots.rating()
  return nodes && nodes.length > 0
})

// Methods
function findContent(nodes) {
  const [{ children }] = nodes
  if (Array.isArray(children)) {
    return findContent(children)
  }
  else {
    return children
  }
}

function onClick() {
  track('Demo clicked', { category: 'Button' })
  forms.value.demo.isVisible = true
}

function renderNode() {
  return h('div', {
    class: 'headline',
    innerHTML: findContent(slots.heading() || [{ children: '' }]),
  })
}
</script>

<style lang="postcss" scoped>
.companies-trust {
  @apply flex flex-col w-full items-center;

  .rating {
    @apply relative flex flex-row w-full justify-center items-center;

    :deep(.stars) {
      @apply flex flex-row mr-2.5;
      transform: translateX(-2px);
    }
  }

  .companies {
    @apply flex flex-wrap justify-around w-full mt-2;
    max-width: 54.625rem;

    @screen md {
      @apply justify-center;
    }

    &.personalized {
      max-width: 100%;

      .logo {
        @apply mt-0 px-0 !important;

        @screen md {
          @apply w-1/3 mb-4 px-0 !important;
        }

        @screen xl {
          @apply w-auto mb-0 px-8  !important;
        }

        &.customizable {
          @apply flex-grow h-auto cursor-pointer;

          @screen xl {
            @apply flex-grow-0;
          }
        }
      }
    }

    .logo {
      @apply flex justify-center w-1/2 px-4 mt-6;

      @screen md {
        @apply px-8 w-1/5 !important;
      }

      @screen lg {
        @apply px-12;
      }

      &.customizable {
        &:hover {
          .placeholder {
            @apply bg-grey-blue-200 border-grey-blue-600;
          }
        }

        .placeholder {
          @apply flex items-center p-3 bg-grey-blue-100 border border-dashed border-grey-blue-300 rounded transition-colors duration-300 ease-in-out;

          &.display-logo {
            @apply p-4;

            .h5 {
              @apply hidden;
            }

            .your-company-logo {
              @apply flex;
            }
          }

          .plus {
            @apply mr-2 rounded-none;
          }

          .h5 {
            @apply text-grey-blue-600;
          }

          .your-company-logo {
            @apply hidden ml-2 h-[30px] w-auto;
          }
        }
      }

      picture {
        @apply rounded-none;
      }
    }
  }

  .headline {
    @apply mt-8 text-grey-blue-600 text-center;

    :deep(strong) {
      @apply font-medium;
    }
  }

  .action {
    @apply mt-6;

    @screen md {
      @apply mt-8;
    }
  }
}
</style>
